import React, { useMemo, useState } from 'react';

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';

import type { MarketingRouterSignificanceTimeseriesDay } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import { METRIC_DISPLAY, type MarketingRouterMetricKey } from './marketingRouterSignificanceDisplay';
import {
  TREND_MODES,
  type TrendMode,
  buildTrendChart,
  formatTrendDate,
  formatTrendValue,
} from './marketingRouterTrend';

const SIGNIFICANCE_THRESHOLD_PERCENT = 95;

/**
 * One metric over the run, as Optimizely draws it: improvement, metric value
 * or confidence by day, one line per variant. Improvement and confidence
 * carry a reference line — zero and the 95% bar — so "settled" and "still
 * wandering" can be read off the shape without the numbers.
 */
export default function MarketingRouterMetricTrendChart({
  metric,
  days,
  baseVariantName,
}: {
  metric: MarketingRouterMetricKey;
  days: MarketingRouterSignificanceTimeseriesDay[];
  baseVariantName: string;
}) {
  const theme = useTheme();
  const [mode, setMode] = useState<TrendMode>('improvement');
  const chart = useMemo(
    () => buildTrendChart(days, metric, mode, baseVariantName),
    [days, metric, mode, baseVariantName],
  );
  const treatmentColours = [theme.palette.warning.main, theme.palette.success.main, theme.palette.secondary.main];
  let treatmentIndex = 0;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2">Over time</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next: TrendMode | null) => {
            if (next) {
              setMode(next);
            }
          }}
          aria-label={`${METRIC_DISPLAY[metric].name} graph mode`}
        >
          {TREND_MODES.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      {chart.rows.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No daily data yet.
        </Typography>
      ) : (
        <Box sx={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chart.rows} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="date" tickFormatter={formatTrendDate} tick={{ fontSize: 12 }} minTickGap={24} />
              <YAxis
                tickFormatter={(value: number) => formatTrendValue(mode, metric, value)}
                tick={{ fontSize: 12 }}
                width={72}
                domain={mode === 'confidence' ? [0, 100] : ['auto', 'auto']}
              />
              <Tooltip
                labelFormatter={(label) => formatTrendDate(String(label))}
                formatter={(value, name) => [
                  typeof value === 'number' ? formatTrendValue(mode, metric, value) : '—',
                  String(name),
                ]}
              />
              <Legend />
              {mode === 'improvement' ? <ReferenceLine y={0} stroke={theme.palette.text.disabled} /> : null}
              {mode === 'confidence' ? (
                <ReferenceLine
                  y={SIGNIFICANCE_THRESHOLD_PERCENT}
                  stroke={theme.palette.success.main}
                  strokeDasharray="4 4"
                  label={{ value: '95%', position: 'right', fontSize: 11, fill: theme.palette.success.main }}
                />
              ) : null}
              {chart.series.map((series) => {
                const colour = series.isControl
                  ? theme.palette.primary.main
                  : treatmentColours[treatmentIndex++ % treatmentColours.length];
                return (
                  <Line
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.label}
                    stroke={colour}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Stack>
  );
}
